import React, { useState, useCallback } from 'react'
import { View, Pressable } from 'react-native'
import { Button, Modal, Portal, TextInput, Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  useCreateCollaboratorListMutation,
  useMyCollaboratorListsQuery,
} from '../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { createStyle } from '../../../../contexts/global-styles.provider'

const LIST_COLOR_OPTIONS = [
  '#C62828', // red
  '#AD1457', // pink
  '#6A1B9A', // purple
  '#283593', // indigo
  '#1565C0', // blue
  '#00695C', // teal
  '#2E7D32', // green
  '#E65100', // orange
] as const

type CreateCollaboratorListModalProps = {
  visible: boolean
  onDismiss: () => void
}

export const CreateCollaboratorListModal: React.FC<
  CreateCollaboratorListModalProps
> = ({ visible, onDismiss }) => {
  const { t } = useTranslation('collaborators')
  const styles = useStyle()
  const [name, setName] = useState('')
  const [color, setColor] = useState('')

  const [createList, { loading }] = useCreateCollaboratorListMutation()
  const { refetch } = useMyCollaboratorListsQuery({
    skip: !visible,
  })

  const handleCreate = useCallback(async () => {
    if (!name.trim()) {
      Toast.show(t('list-name-required'))
      return
    }
    if (!color) {
      Toast.show(t('list-color-required'))
      return
    }

    try {
      await createList({
        variables: {
          input: {
            name: name.trim(),
            color: color.trim() || null,
          },
        },
      })

      Toast.show(t('list-created-successfully'))
      setName('')
      setColor('')
      await refetch()
      onDismiss()
    } catch (e) {
      Toast.show((e as Error).message)
      console.error(e)
      Sentry.captureException(e)
    }
  }, [name, color, createList, refetch, onDismiss, t])

  const handleClose = useCallback(() => {
    setName('')
    setColor('')
    onDismiss()
  }, [onDismiss])

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleClose}
        contentContainerStyle={styles.modalContainer}
      >
        <View style={styles.modalContent}>
          <Text variant="titleLarge" style={styles.modalTitle}>
            {t('create-list')}
          </Text>

          <TextInput
            label={t('list-name')}
            value={name}
            onChangeText={setName}
            mode="outlined"
            style={styles.input}
            autoFocus
          />

          <Text variant="bodyMedium" style={styles.colorLabel}>
            {t('list-color')}
          </Text>
          <View style={styles.colorRow}>
            {LIST_COLOR_OPTIONS.map((hex) => (
              <Pressable
                key={hex}
                onPress={() => setColor(color === hex ? '' : hex)}
                style={[
                  styles.colorChip,
                  { backgroundColor: hex },
                  color === hex && styles.colorChipSelected,
                ]}
              />
            ))}
          </View>

          <View style={styles.buttonRow}>
            <Button
              onPress={handleClose}
              style={styles.button}
              disabled={loading}
            >
              {t('common:cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleCreate}
              style={styles.button}
              loading={loading}
              disabled={loading}
            >
              {t('create')}
            </Button>
          </View>
        </View>
      </Modal>
    </Portal>
  )
}

const useStyle = createStyle(({ theme, isMobile }) => ({
  modalContainer: {
    width: isMobile ? '100%' : '60%',
    alignSelf: 'center',
    backgroundColor: theme.colors.surface,
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderRadius: 8,
  },
  modalContent: {
    minWidth: 300,
  },
  modalTitle: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  input: {
    marginBottom: theme.spacing(2),
  },
  colorLabel: {
    marginBottom: theme.spacing(1),
  },
  colorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  colorChip: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  colorChipSelected: {
    borderWidth: 3,
    borderColor: theme.colors.primary,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  button: {
    flex: 1,
  },
}))
